import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IconPencilPlus, IconEye, IconMessageCircle, IconPin } from '@tabler/icons-react';
import DOMPurify from 'dompurify';
import api from '../api/api';

export default function BoardListPage({ me, showToast }) {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  // 글쓰기 모드
  const [writeMode, setWriteMode] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchPosts = useCallback(async (p = 0) => {
    setLoading(true);
    try {
      const [boardRes, postRes] = await Promise.all([
        api.get('/boards'),
        api.get('/posts', { params: { boardId, page: p, size: 20 } }),
      ]);
      const found = boardRes.data.find(b => b.id === Number(boardId));
      setBoard(found || null);
      setPosts(postRes.data.content);
      setTotalPages(postRes.data.totalPages);
      setPage(p);
    } catch {
      showToast('게시글을 불러오는데 실패했습니다.', 'danger');
    } finally {
      setLoading(false);
    }
  }, [boardId, showToast]);

  useEffect(() => {
    fetchPosts(0);
    setWriteMode(false);
  }, [boardId, fetchPosts]);

  const handleSubmit = async () => {
    if (!title.trim()) { showToast('제목을 입력해주세요.', 'danger'); return; }
    if (!content.trim()) { showToast('내용을 입력해주세요.', 'danger'); return; }
    setSubmitting(true);
    try {
      await api.post('/posts', { boardId: Number(boardId), title: title.trim(), content });
      showToast('게시글이 등록되었습니다.', 'success');
      setWriteMode(false);
      setTitle('');
      setContent('');
      fetchPosts(0);
    } catch (err) {
      showToast(err.response?.data?.error || '등록에 실패했습니다.', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (dt) => dt ? new Date(dt).toLocaleDateString('ko-KR') : '-';

  if (loading && !board) {
    return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;
  }

  // 글쓰기 모드
  if (writeMode) {
    return (
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h3 className="card-title mb-0">{board?.name} - 새 글 작성</h3>
          <button className="btn btn-ghost-secondary btn-sm" onClick={() => setWriteMode(false)}>취소</button>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">제목</label>
            <input type="text" className="form-control form-control-sm" placeholder="제목을 입력하세요"
              value={title} onChange={e => setTitle(e.target.value)} autoFocus />
          </div>
          <div className="mb-3">
            <label className="form-label">내용</label>
            <textarea className="form-control form-control-sm" rows={10} placeholder="내용을 입력하세요"
              value={content} onChange={e => setContent(e.target.value)} />
          </div>
          <button className="btn btn-sm btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? '등록 중...' : '등록'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between">
        <h3 className="card-title mb-0">{board?.name || '게시판'}</h3>
        <button className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={() => setWriteMode(true)}>
          <IconPencilPlus size={15} /> 글쓰기
        </button>
      </div>

      {posts.length === 0 ? (
        <div className="card-body text-center text-muted py-5">게시글이 없습니다.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-vcenter card-table">
            <thead>
              <tr>
                <th style={{ width: '50%' }}>제목</th>
                <th>작성자</th>
                <th className="text-center"><IconEye size={14} /></th>
                <th>작성일</th>
              </tr>
            </thead>
            <tbody>
              {posts.map(post => (
                <tr key={post.id} style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/board/${boardId}/post/${post.id}`)}>
                  <td>
                    <div className="d-flex align-items-center gap-1">
                      {post.isNotice && <span className="badge bg-red-lt me-1"><IconPin size={12} /> 공지</span>}
                      <span style={{ fontWeight: post.isNotice ? 600 : 400 }}>{post.title}</span>
                      {post.replyCount > 0 && (
                        <span className="text-muted small ms-1">
                          <IconMessageCircle size={13} /> {post.replyCount}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="text-muted">{post.authorName}</td>
                  <td className="text-center text-muted">{post.viewCount}</td>
                  <td className="text-muted">{fmt(post.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 페이징 */}
      {totalPages > 1 && (
        <div className="card-footer d-flex justify-content-center">
          <ul className="pagination mb-0">
            {[...Array(totalPages)].map((_, i) => (
              <li key={i} className={`page-item ${i === page ? 'active' : ''}`}>
                <button className="page-link" onClick={() => fetchPosts(i)}>{i + 1}</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
